import { NAP } from "@/lib/nap";

const ORG_ID = `${NAP.siteUrl}/#organization`;
const LOGO_URL = `${NAP.siteUrl}/img/logo.svg`;
const OG_IMAGE = `${NAP.siteUrl}/img/og/siddhivinayak-group-og.jpg`;

const absoluteUrl = (value) => {
  if (!value) return "";
  const url = String(value);
  if (/^https?:\/\//i.test(url)) return url;
  return `${NAP.siteUrl}${url.startsWith("/") ? "" : "/"}${url}`;
};

const organization = {
  "@type": ["Organization", "RealEstateAgent"],
  "@id": ORG_ID,
  name: NAP.brand,
  alternateName: NAP.legalName,
  url: NAP.siteUrl,
  logo: LOGO_URL,
  telephone: NAP.phone,
  email: NAP.email,
  address: {
    "@type": "PostalAddress",
    streetAddress: NAP.streetAddress,
    addressLocality: NAP.addressLocality,
    addressRegion: NAP.addressRegion,
    postalCode: NAP.postalCode,
    addressCountry: NAP.addressCountry,
  },
};

/**
 * Build a Residence schema for /projects/[slug]. Falls back gracefully
 * when the product object is missing optional fields (location, area,
 * bedrooms, price). Fields that aren't known are simply omitted from
 * the @graph — never emitted as empty strings, which would be
 * rejected by Google Rich Results validation.
 */
export function buildProjectDetailSchema(product) {
  if (!product) return { "@context": "https://schema.org", "@graph": [organization] };
  const slug = product.slug || product.id || "";
  const projectId = `${NAP.siteUrl}/projects/${slug}#residence`;
  const detailUrl = `${NAP.siteUrl}/projects/${slug}`;

  const residence = {
    "@type": "ApartmentComplex",
    "@id": projectId,
    name: product.title || product.heading || "Siddhivinayak Group Project",
    description: product.excerpt || product.card_blurb || "",
    url: detailUrl,
    image: absoluteUrl(product.featured_image || product.productImg) || OG_IMAGE,
    ...(product.author?.name
      ? { provider: { "@type": "Organization", name: NAP.brand, "@id": ORG_ID } }
      : {}),
  };

  // Optional address — only emit if location data is present
  const suppliedAddress = product.address;
  const locality = pickDetail(product, ["location", "locality", "area"]);
  if (suppliedAddress && typeof suppliedAddress === "object") {
    residence.address = {
      "@type": "PostalAddress",
      ...suppliedAddress,
    };
  } else if (locality) {
    residence.address = {
      "@type": "PostalAddress",
      addressLocality: locality,
      addressRegion: "Maharashtra",
      addressCountry: "IN",
    };
  }

  // Optional number of bedrooms — only emit if a positive integer
  const bedroomOptions = parseBedroomOptions(product);
  if (bedroomOptions.length) {
    residence.containsPlace = bedroomOptions.map((beds) => ({
      "@type": "Apartment",
      name: `${beds} BHK apartment at ${product.title || "the project"}`,
      numberOfBedrooms: beds,
    }));
  }

  const amenities = (Array.isArray(product.amenities1) ? product.amenities1 : [])
    .map((item) =>
      typeof item === "string" ? item : item && typeof item === "object" ? item.title : ""
    )
    .filter(Boolean);
  if (amenities.length) {
    residence.amenityFeature = amenities.map((name) => ({
      "@type": "LocationFeatureSpecification",
      name,
      value: true,
    }));
  }

  // Optional floor size
  const area = pickDetail(product, ["area", "size", "carpet_area", "sqft"]);
  if (area) {
    const m = String(area).match(/\d+/);
    if (m) {
      residence.floorSize = {
        "@type": "QuantitativeValue",
        value: Number(m[0]),
        unitText: "sqft",
      };
    }
  }

  const breadcrumb = {
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: NAP.siteUrl },
      {
        "@type": "ListItem",
        position: 2,
        name: "Projects",
        item: `${NAP.siteUrl}/projects`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: product.title || "Project",
        item: detailUrl,
      },
    ],
  };

  const faqEntries = (Array.isArray(product.faqs) ? product.faqs : [])
    .map((item) => ({
      question: String(item?.question || "").trim(),
      answer: String(item?.answer || "").trim(),
    }))
    .filter((item) => item.question && item.answer);

  const faqPage = faqEntries.length
    ? {
        "@type": "FAQPage",
        "@id": `${detailUrl}#faq`,
        mainEntity: faqEntries.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      }
    : null;

  return {
    "@context": "https://schema.org",
    "@graph": [organization, residence, breadcrumb, faqPage].filter(Boolean),
  };
}

function pickDetail(obj, keys) {
  for (const k of keys) {
    const v = obj?.[k];
    if (v && String(v).trim()) return String(v).trim();
  }
  return null;
}

function parseBedroomOptions(product) {
  // Supports a single value ("2 BHK") or a range/list ("2 BHK & 3 BHK").
  const raw = pickDetail(product, ["bedrooms", "bhk", "configuration", "type"]);
  if (!raw) return [];
  return [...new Set((String(raw).match(/\d+/g) || []).map(Number))].filter(
    (value) => Number.isInteger(value) && value > 0
  );
}
